/**
 * cloudBackupService.js
 * 提供云端备份和恢复功能，使用腾讯云COS存储
 */

import COS from 'cos-js-sdk-v5';

// 腾讯云COS配置
const COS_CONFIG = {
  SecretId: 'AKIDZ5OG3iDXfdPzObiePyLVYZO0QipidQzc',
  SecretKey: 'f8cy87vY2KRM5dz98vWwAC2g9QWP5Yr1',
  Bucket: 'xiaobaiwu-1317821739',
  Region: 'ap-chengdu',
  BaseUrl: 'https://xiaobaiwu-1317821739.cos.ap-chengdu.myqcloud.com'
};

// 初始化COS实例
const cos = new COS({
  SecretId: COS_CONFIG.SecretId,
  SecretKey: COS_CONFIG.SecretKey,
});

/**
 * 生成唯一的备份ID
 * @returns {string} 备份ID
 */
function generateBackupId() {
  // 生成一个随机的UUID
  return 'bkp-' + ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (((c ^ crypto.getRandomValues(new Uint8Array(1))[0]) & 15) >> (c / 4)).toString(16)
  );
}

/**
 * 生成验证码
 * @returns {string} 6位数字验证码
 */
function generateVerificationCode() {
  // 生成6位数字验证码
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * 创建备份元数据
 * @param {Object} data 要备份的数据
 * @returns {Object} 备份元数据
 */
function createBackupMetadata(data) {
  return {
    promptCount: data.prompts ? data.prompts.length : 0,
    folderCount: data.folders ? data.folders.length : 0,
    tagCount: data.tags ? data.tags.length : 0,
    creationDate: new Date().toISOString(),
    // 添加一些示例提示词标题，帮助用户识别
    samplePrompts: data.prompts && data.prompts.length > 0 ? 
      data.prompts.slice(0, 3).map(p => p.title) : []
  };
}

/**
 * 将备份历史保存到本地存储
 * @param {Object} backupInfo 备份信息
 */
function saveBackupHistory(backupInfo) {
  // 从localStorage获取现有历史
  const historyJson = localStorage.getItem('backup_history') || '[]';
  let history = [];
  try {
    history = JSON.parse(historyJson);
  } catch (e) {
    history = [];
  }
  
  // 添加新备份记录
  history.push({
    id: backupInfo.backupId,
    date: new Date().toISOString(),
    metadata: backupInfo.metadata,
    verificationCode: backupInfo.verificationCode // 保存验证码以便用户恢复
  });
  
  // 只保留最近的10条记录
  if (history.length > 10) {
    history = history.slice(history.length - 10);
  }
  
  // 保存回localStorage
  localStorage.setItem('backup_history', JSON.stringify(history));
}

/**
 * 获取本地备份历史
 * @returns {Array} 备份历史记录
 */
function getBackupHistory() {
  const historyJson = localStorage.getItem('backup_history') || '[]';
  try {
    return JSON.parse(historyJson);
  } catch (e) {
    console.error('解析备份历史失败:', e);
    return [];
  }
}

/**
 * 从本地备份历史中删除指定备份记录
 * @param {string} backupId 要删除的备份ID
 * @returns {boolean} 删除是否成功
 */
function deleteBackupHistory(backupId) {
  try {
    // 获取当前备份历史
    const historyJson = localStorage.getItem('backup_history') || '[]';
    let history = JSON.parse(historyJson);
    
    // 查找并删除指定记录
    const newHistory = history.filter(item => item.id !== backupId);
    
    // 保存更新后的历史
    localStorage.setItem('backup_history', JSON.stringify(newHistory));
    return true;
  } catch (e) {
    console.error('删除备份历史失败:', e);
    return false;
  }
}

/**
 * 创建云端备份
 * @param {Object} data 要备份的数据
 * @returns {Promise<Object>} 备份信息
 */
async function createCloudBackup(data) {
  try {
    const backupId = generateBackupId();
    const verificationCode = generateVerificationCode();
    const metadata = createBackupMetadata(data);
    
    // 准备完整的备份数据
    const backupData = {
      backupId,
      metadata,
      verificationHash: btoa(verificationCode), // 简单加密验证码
      data,
      createdAt: new Date().toISOString()
    };
    
    // 上传到腾讯云COS
    const key = `backups/${backupId}.json`;
    await new Promise((resolve, reject) => {
      cos.putObject({
        Bucket: COS_CONFIG.Bucket,
        Region: COS_CONFIG.Region,
        Key: key,
        Body: JSON.stringify(backupData),
        ContentType: 'application/json',
      }, function(err, data) {
        if(err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
    
    // 准备返回的备份信息
    const backupInfo = {
      backupId,
      verificationCode,
      metadata,
      url: `${COS_CONFIG.BaseUrl}/${key}`,
      createdAt: backupData.createdAt
    };
    
    // 保存备份历史到本地
    saveBackupHistory(backupInfo);
    
    return backupInfo;
  } catch (error) {
    console.error('创建云端备份失败:', error);
    throw new Error('创建云端备份失败: ' + error.message);
  }
}

/**
 * 获取备份元数据
 * @param {string} backupId 备份ID
 * @returns {Promise<Object>} 备份元数据
 */
async function getBackupMetadata(backupId) {
  try {
    const key = `backups/${backupId}.json`;
    
    const result = await new Promise((resolve, reject) => {
      cos.getObject({
        Bucket: COS_CONFIG.Bucket,
        Region: COS_CONFIG.Region,
        Key: key,
      }, function(err, data) {
        if(err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
    
    const backupData = JSON.parse(result.Body);
    return {
      backupId: backupData.backupId,
      metadata: backupData.metadata,
      createdAt: backupData.createdAt
    };
  } catch (error) {
    console.error('获取备份元数据失败:', error);
    throw new Error('获取备份元数据失败: ' + error.message);
  }
}

/**
 * 验证并恢复备份
 * @param {string} backupId 备份ID
 * @param {string} verificationCode 验证码
 * @returns {Promise<Object>} 恢复的数据
 */
async function verifyAndRestoreBackup(backupId, verificationCode) {
  try {
    const key = `backups/${backupId}.json`;
    
    const result = await new Promise((resolve, reject) => {
      cos.getObject({
        Bucket: COS_CONFIG.Bucket,
        Region: COS_CONFIG.Region,
        Key: key,
      }, function(err, data) {
        if(err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
    
    const backupData = JSON.parse(result.Body);
    
    // 验证验证码
    if (btoa(verificationCode) !== backupData.verificationHash) {
      throw new Error('验证码不正确');
    }
    
    return backupData.data;
  } catch (error) {
    console.error('验证并恢复备份失败:', error);
    throw new Error('验证并恢复备份失败: ' + error.message);
  }
}

/**
 * 导出备份信息到文件
 * @param {Object} backupInfo 备份信息
 */
function exportBackupInfo(backupInfo) {
  // 创建包含备份信息的对象
  const exportData = {
    backupId: backupInfo.backupId || backupInfo.id, // 支持从历史记录导出
    verificationCode: backupInfo.verificationCode,
    metadata: backupInfo.metadata,
    exportDate: new Date().toISOString()
  };
  
  // 确保backupId存在
  if (!exportData.backupId) {
    throw new Error('备份ID缺失，无法导出');
  }
  
  // 转换为JSON
  const jsonString = JSON.stringify(exportData, null, 2);
  
  // 创建下载链接
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  // 创建下载链接并点击
  const a = document.createElement('a');
  a.href = url;
  a.download = `PromptPro备份信息_${new Date().toLocaleDateString().replace(/\//g, '-')}.json`;
  document.body.appendChild(a);
  a.click();
  
  // 清理
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * 从文件导入备份信息
 * @param {File} file 备份信息文件
 * @returns {Promise<Object>} 备份信息
 */
async function importBackupInfo(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const backupInfo = JSON.parse(e.target.result);
        // 验证备份信息格式
        if (!backupInfo.backupId || !backupInfo.verificationCode) {
          reject(new Error('无效的备份信息文件'));
          return;
        }
        
        resolve(backupInfo);
      } catch (error) {
        reject(new Error('无法解析备份信息文件'));
      }
    };
    
    reader.onerror = () => reject(new Error('读取文件失败'));
    
    reader.readAsText(file);
  });
}

// 导出服务函数
const CloudBackupService = {
  createCloudBackup,
  getBackupMetadata,
  verifyAndRestoreBackup,
  exportBackupInfo,
  importBackupInfo,
  getBackupHistory,
  deleteBackupHistory
};

export default CloudBackupService;
